import { useAuth } from "@clerk/clerk-react";
import { Avatar, Box, IconButton, Typography } from "@mui/material";
import { ActionItemsContext } from "contexts/ActionItems";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import moment from "moment";
import { useContext } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { ActionItem, ActionItemType } from "schemas/dashboard";
import { CustomEvent } from "schemas/functions";

import OutreachContactDrawer from "features/Influencer/ContactList/OutreachContactDrawer";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import ListSkeleton from "./ListSkeleton";
import styles from "./styles";

interface Props {
  fetchLoading: boolean;
}

const ActionItemList = ({ fetchLoading }: Props) => {
  const navigate = useNavigate();
  const { setErrorAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const { handleOpenQuickSendIndividual } = useContext(QuickSendDrawerContext);
  const { actionItems, setActionItems } = useContext(ActionItemsContext);

  const handleArchive = async (e: CustomEvent, actionItem: ActionItem) => {
    e.stopPropagation();

    setActionItems((prev) => prev.filter((item) => item.id !== actionItem.id));
    try {
      await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/action-items/${actionItem.id}/archive`,
        "POST",
      );
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const openDrawer = (actionItem: ActionItem) => {
    trackEvent("Action Item Clicked", {
      "Action Item ID": actionItem.id,
      "Action Item Type": actionItem.type,
      "Outreach Contact ID": actionItem.outreachContactId,
    });
    if (
      actionItem.type === ActionItemType.new_contact_outreach &&
      actionItem?.outreachContact?.bentoBrandId
    ) {
      handleOpenQuickSendIndividual(
        actionItem?.outreachContact?.bentoBrandId,
        "Action Item",
        undefined,
        true,
      );
    } else {
      navigate(
        `action/${actionItem.outreachContactId}/email/${actionItem?.gmailThreadId}`,
      );
    }
  };

  const getDifference = (actionItem: ActionItem) => {
    const today = moment();
    const start =
      actionItem?.pendingAt ||
      actionItem?.outreachContact?.lastGmailMessageDate ||
      0;
    if (start === 0) return "";
    const lastEmailDate = moment(start * 1000);
    const duration = moment.duration(today.diff(lastEmailDate));
    const days = duration.asDays();

    if (days >= 30) {
      const month = duration.asMonths();

      return (
        `${month.toFixed(0)}` +
        (Number(month.toFixed(0)) < 2 ? ` month ago` : ` months ago`)
      );
    } else if (days >= 7) {
      const week = duration.asWeeks();
      return (
        `${week.toFixed(0)}` +
        (Number(week.toFixed(0)) < 2 ? ` week ago` : ` weeks ago`)
      );
    } else if (days >= 1) {
      return (
        `${days.toFixed(0)}` +
        (Number(days.toFixed(0)) < 2 ? ` day ago` : ` days ago`)
      );
    } else {
      return "";
    }
  };

  const getStatus = (actionItem: ActionItem) => {
    const difference = getDifference(actionItem);
    const outreachContact = actionItem?.outreachContact;

    if (actionItem.type === ActionItemType.brand_response) {
      return {
        label: difference ? `Replied ${difference}` : "Replied",
        active: true,
      };
    }
    if (actionItem.type === ActionItemType.new_contact_outreach) {
      return { label: "Ready to pitch", active: false };
    }
    if (outreachContact?.lastEmailOpenCount) {
      return {
        label: difference
          ? `Viewed your pitch · ${difference}`
          : "Viewed your pitch",
        active: false,
      };
    }
    return {
      label: difference ? `No response · ${difference}` : "Awaiting reply",
      active: false,
    };
  };

  if (fetchLoading) {
    return <ListSkeleton />;
  }

  if (!actionItems?.length) {
    return null;
  }

  const count = actionItems.length;

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Box sx={styles.iconBadge}>
            <Box component="i" className="fa-regular fa-reply" />
            <Box sx={styles.iconDot} />
          </Box>
          <Box sx={styles.todayPill}>
            <Box component="i" className="fa-solid fa-arrow-trend-up" />
            <Box component="span" sx={styles.todayCount}>
              {count}
            </Box>
            TODAY
          </Box>
        </Box>

        <Typography sx={styles.title}>Follow-ups &amp; Replies</Typography>

        <Box sx={styles.subheader}>
          <Box sx={styles.subheaderDot} />
          <Typography component="span" sx={styles.subheaderText}>
            <b>
              {count} {count === 1 ? "brand" : "brands"}
            </b>{" "}
            need a follow-up today
          </Typography>
        </Box>

        <Box sx={styles.list}>
          {actionItems.map((actionItem) => {
            const status = getStatus(actionItem);
            const outreachContact = actionItem?.outreachContact;
            const threadLength = outreachContact?.lastGmailThreadLength;
            const snippet = outreachContact?.lastGmailMessageSnippet;

            return (
              <Box
                key={actionItem.id}
                sx={styles.item}
                onClick={() => openDrawer(actionItem)}
              >
                <Box sx={styles.itemMain}>
                  <Avatar
                    src={outreachContact?.bentoBrand?.logoUrl}
                    sx={styles.avatar(status.active)}
                  >
                    {actionItem?.brandName?.[0]?.toUpperCase()}
                  </Avatar>

                  <Box sx={styles.itemContent}>
                    <Box sx={styles.itemTopRow}>
                      <Box sx={styles.itemTitleGroup}>
                        <Typography sx={styles.brandName}>
                          {actionItem?.brandName}
                        </Typography>
                        {threadLength ? (
                          <Box sx={styles.msgChip}>
                            <Box
                              component="i"
                              className="fa-regular fa-envelope"
                            />
                            {threadLength} {threadLength === 1 ? "msg" : "msgs"}
                          </Box>
                        ) : null}
                      </Box>

                      <Box sx={styles.statusGroup}>
                        <Typography sx={styles.status(status.active)}>
                          {status.label}
                        </Typography>
                        {status.active && <Box sx={styles.statusDot} />}
                      </Box>
                    </Box>

                    {snippet && (
                      <Typography
                        sx={styles.snippet}
                        dangerouslySetInnerHTML={{
                          __html: `“${snippet}…”`,
                        }}
                      />
                    )}
                  </Box>
                </Box>

                <IconButton
                  className="action-item-archive"
                  sx={styles.archive}
                  onClick={(event) => handleArchive(event, actionItem)}
                >
                  <Box component="i" className="fa-regular fa-xmark" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Routes>
        <Route
          path="action/:outreachContactId/*"
          element={<OutreachContactDrawer />}
        />
      </Routes>
    </Box>
  );
};

export default ActionItemList;
